"use client";

/**
 * KortingBarBlock — "Claim uw korting" CTA-bar met 6 varianten.
 *
 * Port van Claude Design "Korting Blok.html":
 *   - editorial      : rustig cream + italic headline + eyebrow
 *   - age-calculator : interactieve slider "€15 per levensjaar"
 *   - voucher        : knipbon met perforatie + grote amount-stub
 *   - dark-premium   : dark + gouden zegel met bedrag
 *   - oversized      : magazine-stijl met enorm cijfer (€1500)
 *   - sticker        : cirkel-sticker "Gratis montage" op cream kaart
 *
 * Kleuren volgen thema CSS vars. Deadline-tekst, bedrag, rate-per-jaar,
 * kopjes en CTA zijn allemaal overschrijfbaar per content.
 */

import { useState } from "react";

export type KortingVariant =
  | "editorial"
  | "age-calculator"
  | "voucher"
  | "dark-premium"
  | "oversized"
  | "sticker";

export interface KortingContent {
  eyebrow?: string;
  headline?: string;
  headline_italic?: string;
  body?: string;
  deadline?: string;
  amount?: string;
  amount_words?: string;
  cta_text?: string;
  cta_href?: string;
  /** voor 'age-calculator': bedrag per levensjaar (default 15). */
  rate_per_year?: number;
  /** voor 'age-calculator': min/max/initial leeftijd. */
  age_min?: number;
  age_max?: number;
  age_initial?: number;
  /** voor 'sticker' + 'dark-premium'. */
  badge_title?: string;
  badge_subtitle?: string;
  badge_sub_subtitle?: string;
  /** Klein cursief disclaimer onder CTA. Default 'Geldig op alle badkameroplossingen.' */
  footnote?: string;
}

interface Props {
  content: KortingContent;
  variant?: KortingVariant;
}

const T = {
  bg: "var(--cream, #F1EEE7)",
  bgWarm: "var(--cream-dark, #EDE3D0)",
  bgDark: "#1A1F26",
  card: "#FFFFFF",
  ink: "var(--ink, #141C28)",
  muted: "var(--stone, #5A6578)",
  border: "rgba(20,28,40,0.10)",
  cta: "var(--terracotta, #FF6B1A)",
  gold: "#B8925A",
  goldLight: "#E3C68A",
  red: "#B33A2A",
};

function Arrow({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
function ClockIcon({ size = 13, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function DeadlineChip({ deadline, dark }: { deadline: string; dark?: boolean }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 14px",
        borderRadius: 999,
        background: dark ? "rgba(255,107,26,0.14)" : "rgba(255,107,26,0.10)",
        border: `1px solid ${dark ? "rgba(255,107,26,0.4)" : "rgba(255,107,26,0.3)"}`,
        color: T.cta,
        fontFamily: "system-ui",
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: "0.04em",
      }}
    >
      <ClockIcon /> Nog tot {deadline}
    </div>
  );
}

function Cta({ content, dark }: { content: KortingContent; dark?: boolean }) {
  return (
    <a
      href={content.cta_href ?? "#brochure-form"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "15px 28px",
        borderRadius: 999,
        background: T.cta,
        color: "white",
        fontFamily: "system-ui",
        fontSize: 15.5,
        fontWeight: 600,
        textDecoration: "none",
        boxShadow: dark
          ? "0 14px 28px -12px rgba(255,107,26,0.6)"
          : "0 12px 24px -12px rgba(255,107,26,0.55)",
      }}
    >
      {content.cta_text ?? "Claim uw korting"} <Arrow />
    </a>
  );
}

// ── Variant 1: editorial rustig cream ─────────────────────────────────────
function Editorial({ content }: { content: KortingContent }) {
  const deadline = content.deadline ?? `30 november ${new Date().getFullYear()}`;
  return (
    <section style={{ background: "#F6F2EA", padding: "56px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .krt-ed { flex-direction: column !important; text-align: center !important; gap: 22px !important; } }`}</style>
      <div className="krt-ed" style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", gap: 48 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600, color: T.muted, marginBottom: 14 }}>
            {content.eyebrow ?? `Actie — tot ${deadline}`}
          </div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(24px, 3vw, 40px)", fontWeight: 400, color: T.ink, lineHeight: 1.1, letterSpacing: "-0.015em", margin: "0 0 10px", textWrap: "balance" }}>
            {content.headline ?? (
              <>
                Deze maand <span style={{ fontStyle: "italic" }}>{content.headline_italic ?? "tot wel €1.500 korting"}</span> op uw complete badkamer.
              </>
            )}
          </h3>
          <p style={{ fontSize: 15.5, color: T.muted, lineHeight: 1.55, margin: 0, maxWidth: 560 }}>
            {content.body ?? "Geldt bij een opdracht die vóór de actie-einddatum wordt bevestigd. Vraag vrijblijvend de brochure aan — geen verplichting."}
          </p>
        </div>
        <div style={{ flexShrink: 0 }}>
          <Cta content={content} />
        </div>
      </div>
    </section>
  );
}

// ── Variant 2: age calculator ─────────────────────────────────────────────
function AgeCalculator({ content }: { content: KortingContent }) {
  const rate = content.rate_per_year ?? 15;
  const ageMin = content.age_min ?? 30;
  const ageMax = content.age_max ?? 85;
  const ageInit = content.age_initial ?? 58;
  const [age, setAge] = useState(ageInit);
  const discount = Math.max(0, age * rate);
  return (
    <section style={{ background: `linear-gradient(135deg, ${T.bgWarm} 0%, #E4D6BC 100%)`, padding: "48px 28px", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .krt-age { grid-template-columns: 1fr !important; gap: 26px !important; text-align: center; } }`}</style>
      <div className="krt-age" style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 48, alignItems: "center" }}>
        <div>
          {content.deadline ? (
            <div style={{ marginBottom: 12 }}>
              <DeadlineChip deadline={content.deadline} />
            </div>
          ) : null}
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(24px, 3vw, 38px)", fontWeight: 500, color: T.ink, lineHeight: 1.05, letterSpacing: "-0.015em", margin: "0 0 10px", textWrap: "balance" }}>
            {content.headline ? (
              content.headline
            ) : (
              <>
                <span style={{ color: T.cta, fontWeight: 600 }}>€{rate}</span> korting <span style={{ fontStyle: "italic" }}>per levensjaar.</span>
              </>
            )}
          </h3>
          <p style={{ fontSize: 15.5, color: T.muted, lineHeight: 1.55, margin: "0 0 20px", maxWidth: 420 }}>
            {content.body ?? (
              <>
                Wordt u {ageInit + 4}? Dan trekt u <b style={{ color: T.ink }}>€{((ageInit + 4) * rate).toLocaleString("nl-NL")} korting</b> af van uw nieuwe badkamer. Hoe ouder, hoe meer voordeel.
              </>
            )}
          </p>
          <Cta content={content} />
          <p style={{ fontSize: 12, color: T.muted, marginTop: 14, fontStyle: "italic" }}>
            {content.footnote ?? "Geldig op alle badkameroplossingen."}
          </p>
        </div>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "24px 28px", boxShadow: "0 18px 36px -24px rgba(0,0,0,0.2)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", color: T.muted, textTransform: "uppercase", marginBottom: 10 }}>
            Reken uit · uw leeftijd
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14 }}>
            <span style={{ fontFamily: "'Fraunces', serif", fontSize: 56, fontWeight: 500, color: T.ink, lineHeight: 1, letterSpacing: "-0.02em" }}>{age}</span>
            <span style={{ fontSize: 14, color: T.muted, fontStyle: "italic", fontFamily: "'Fraunces', serif" }}>jaar</span>
          </div>
          <input
            type="range"
            min={ageMin}
            max={ageMax}
            value={age}
            onChange={(e) => setAge(parseInt(e.target.value))}
            aria-label={`Uw leeftijd: ${age} jaar — sleep om uw korting te berekenen`}
            style={{ width: "100%", accentColor: "var(--terracotta, #FF6B1A)", marginBottom: 18 }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: T.bgWarm, borderRadius: 10 }}>
            <span style={{ fontSize: 13, color: T.muted, fontWeight: 600 }}>Uw korting</span>
            <span style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 600, color: T.cta, letterSpacing: "-0.01em" }}>
              €{discount.toLocaleString("nl-NL")}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 3: voucher ─────────────────────────────────────────────────────
function Voucher({ content }: { content: KortingContent }) {
  const deadline = content.deadline ?? `30 november ${new Date().getFullYear()}`;
  const amount = content.amount ?? "€1.500";
  return (
    <section style={{ background: T.bg, padding: "48px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .krt-vou { grid-template-columns: 1fr !important; } .krt-vou > :first-child { border-right: none !important; border-bottom: 2px dashed rgba(20,28,40,0.15) !important; text-align: center !important; } .krt-vou-perf { display: none !important; } }`}</style>
      <div className="krt-vou" style={{ maxWidth: 1000, margin: "0 auto", background: T.card, border: `2px dashed ${T.border}`, borderRadius: 14, boxShadow: "0 22px 44px -28px rgba(0,0,0,0.24)", display: "grid", gridTemplateColumns: "1fr auto 1.4fr", alignItems: "stretch" }}>
        <div style={{ padding: "30px 32px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: T.cta, textTransform: "uppercase", marginBottom: 8 }}>
            {content.eyebrow ?? `Tegoedbon · ${new Date().getFullYear()}`}
          </div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 500, color: T.ink, lineHeight: 1, letterSpacing: "-0.02em" }}>
            {amount}
          </div>
          {content.amount_words ? (
            <div style={{ fontSize: 14, color: T.muted, fontFamily: "'Fraunces', serif", fontStyle: "italic", marginTop: 4 }}>
              {content.amount_words}
            </div>
          ) : (
            <div style={{ fontSize: 14, color: T.muted, fontFamily: "'Fraunces', serif", fontStyle: "italic", marginTop: 4 }}>
              t.w.v. vijftienhonderd euro
            </div>
          )}
        </div>
        <div className="krt-vou-perf" style={{ width: 0, position: "relative" }}>
          <div aria-hidden style={{ position: "absolute", top: 12, bottom: 12, left: -1, borderLeft: `2px dashed ${T.border}` }} />
          <div aria-hidden style={{ position: "absolute", top: -10, left: -10, width: 20, height: 20, borderRadius: "50%", background: T.bg }} />
          <div aria-hidden style={{ position: "absolute", bottom: -10, left: -10, width: 20, height: 20, borderRadius: "50%", background: T.bg }} />
        </div>
        <div style={{ padding: "28px 36px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 10 }}>
          <div>
            <DeadlineChip deadline={deadline} />
          </div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(20px, 2.4vw, 26px)", fontWeight: 500, color: T.ink, lineHeight: 1.15, letterSpacing: "-0.01em", margin: 0 }}>
            {content.headline ?? (
              <>
                Claim uw <span style={{ fontStyle: "italic" }}>korting-bon</span> nu.
              </>
            )}
          </h3>
          <p style={{ fontSize: 14, color: T.muted, margin: 0, lineHeight: 1.55 }}>
            {content.body ?? "Geldig op uw complete badkamer-renovatie · 1 per huishouden."}
          </p>
          <div style={{ marginTop: 4 }}>
            <Cta content={content} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 4: dark premium met gouden zegel ─────────────────────────────
function DarkPremium({ content }: { content: KortingContent }) {
  const deadline = content.deadline ?? `30 november ${new Date().getFullYear()}`;
  const amount = content.amount ?? "€1.500";
  return (
    <section style={{ background: T.bgDark, color: "#F1EEE7", padding: "54px 28px", fontFamily: "system-ui, sans-serif", position: "relative", overflow: "hidden" }}>
      <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(circle at 88% 40%, rgba(184,146,90,0.18) 0%, transparent 55%)" }} />
      <style>{`@media (max-width: 1024px){ .krt-dark { flex-direction: column !important; text-align: center !important; } }`}</style>
      <div className="krt-dark" style={{ position: "relative", maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 44 }}>
        <div style={{ width: 148, height: 148, flexShrink: 0, borderRadius: "50%", background: `radial-gradient(circle at 32% 28%, ${T.goldLight} 0%, ${T.gold} 55%, #8B6D3C 100%)`, boxShadow: "0 14px 28px -10px rgba(184,146,90,0.5), inset 0 0 0 2px rgba(255,255,255,0.2)", display: "grid", placeItems: "center", position: "relative", transform: "rotate(-6deg)" }}>
          <div style={{ position: "absolute", inset: 8, border: "1.5px solid rgba(20,28,40,0.35)", borderRadius: "50%" }} />
          <div style={{ fontFamily: "'Fraunces', serif", textAlign: "center", lineHeight: 1, color: "#141C28" }}>
            <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700 }}>
              {content.badge_title ?? "Voordeel"}
            </div>
            <div style={{ fontSize: 34, fontWeight: 600, marginTop: 4, letterSpacing: "-0.02em" }}>{content.badge_subtitle ?? amount}</div>
            <div style={{ fontSize: 11, fontStyle: "italic", marginTop: 4, fontWeight: 500 }}>{content.badge_sub_subtitle ?? "korting"}</div>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 10 }}>
            <DeadlineChip deadline={deadline} dark />
          </div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(22px, 2.8vw, 36px)", fontWeight: 500, color: "#F1EEE7", lineHeight: 1.1, letterSpacing: "-0.01em", margin: "0 0 8px", textWrap: "balance" }}>
            {content.headline ?? (
              <>
                Uw voordeel, <span style={{ fontStyle: "italic", color: T.goldLight }}>op het eerste moment.</span>
              </>
            )}
          </h3>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: "rgba(241,238,231,0.72)", margin: "0 0 18px", maxWidth: 500 }}>
            {content.body ?? "Claim uw korting direct bij uw eerste adviesgesprek — geen voorwaarden, geen kleine lettertjes."}
          </p>
          <Cta content={content} dark />
        </div>
      </div>
    </section>
  );
}

// ── Variant 5: oversized magazine cijfer ─────────────────────────────────
function Oversized({ content }: { content: KortingContent }) {
  const deadline = content.deadline ?? `30 november ${new Date().getFullYear()}`;
  const amount = content.amount ?? "€1500";
  const [currency, number] = amount.startsWith("€") ? ["€", amount.slice(1)] : ["", amount];
  return (
    <section style={{ background: T.card, padding: "56px 28px", fontFamily: "system-ui, sans-serif", borderTop: `2px solid ${T.ink}`, borderBottom: `1px solid ${T.border}` }}>
      <style>{`@media (max-width: 1024px){ .krt-os { grid-template-columns: 1fr !important; gap: 18px !important; text-align: center; } .krt-os-big { font-size: 110px !important; justify-content: center !important; } }`}</style>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 12, marginBottom: 30, borderBottom: `1px solid ${T.border}`, fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: T.muted }}>
            <span>{content.eyebrow ?? "Aanbieding · Editie November"}</span>
            <span style={{ color: T.cta }}>Tijdelijk</span>
        </div>
        <div className="krt-os" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 48, alignItems: "center" }}>
          <div className="krt-os-big" style={{ fontFamily: "'Fraunces', serif", fontSize: 200, fontWeight: 400, color: T.ink, lineHeight: 0.85, letterSpacing: "-0.045em", display: "flex", alignItems: "baseline" }}>
            {currency ? <span style={{ color: T.cta }}>{currency}</span> : null}
            {number}
          </div>
          <div>
            <div style={{ display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: T.red, textTransform: "uppercase", padding: "5px 10px", border: `1.5px solid ${T.red}`, marginBottom: 14 }}>
              {content.badge_title ?? "Uw directe korting"}
            </div>
            <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(22px, 2.6vw, 34px)", fontWeight: 500, color: T.ink, lineHeight: 1.1, letterSpacing: "-0.01em", margin: "0 0 8px" }}>
              {content.headline ?? (
                <>
                  Op uw complete badkamer — <span style={{ fontStyle: "italic" }}>nu of nooit.</span>
                </>
              )}
            </h3>
            <p style={{ fontSize: 15, color: T.muted, lineHeight: 1.55, margin: "0 0 18px" }}>
              {content.body ?? (
                <>
                  Nog te claimen tot <b style={{ color: T.ink }}>{deadline}</b>. Eén per huishouden, te verrekenen op uw eerste offerte.
                </>
              )}
            </p>
            <Cta content={content} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 6: sticker ───────────────────────────────────────────────────
function Sticker({ content }: { content: KortingContent }) {
  const deadline = content.deadline ?? `30 november ${new Date().getFullYear()}`;
  return (
    <section style={{ background: T.bg, padding: "44px 28px", fontFamily: "system-ui, sans-serif", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
      <style>{`@media (max-width: 1024px){ .krt-sticker-wrap { padding: 32px 22px 30px !important; } .krt-sticker-circle { position: relative !important; left: auto !important; top: auto !important; transform: rotate(-8deg) !important; margin: 0 auto 18px !important; } .krt-sticker-body { text-align: center !important; } }`}</style>
      <div className="krt-sticker-wrap" style={{ maxWidth: 1200, margin: "0 auto", position: "relative", background: T.card, borderRadius: 16, padding: "36px 48px 36px 240px", boxShadow: "0 20px 40px -28px rgba(0,0,0,0.2)", border: `1px solid ${T.border}` }}>
        <div
          className="krt-sticker-circle"
          style={{
            position: "absolute",
            left: 40,
            top: "50%",
            transform: "translateY(-50%) rotate(-8deg)",
            width: 160,
            height: 160,
            borderRadius: "50%",
            background: T.cta,
            color: "white",
            display: "grid",
            placeItems: "center",
            boxShadow: "0 14px 26px -10px rgba(255,107,26,0.55)",
          }}
        >
          <div aria-hidden style={{ position: "absolute", inset: 8, border: "1.5px dashed rgba(255,255,255,0.55)", borderRadius: "50%" }} />
          <div style={{ textAlign: "center", fontFamily: "'Fraunces', serif", color: "white", lineHeight: 1 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700 }}>
              {content.badge_title ?? "Gratis"}
            </div>
            <div style={{ fontSize: 30, fontWeight: 600, margin: "6px 0 4px", fontStyle: "italic" }}>
              {content.badge_subtitle ?? "montage"}
            </div>
            <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
              {content.badge_sub_subtitle ?? "t.w.v. €2.500"}
            </div>
          </div>
        </div>
        <div className="krt-sticker-body">
          <div style={{ marginBottom: 10 }}>
            <DeadlineChip deadline={deadline} />
          </div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(22px, 2.6vw, 32px)", fontWeight: 500, color: T.ink, lineHeight: 1.1, letterSpacing: "-0.01em", margin: "0 0 8px" }}>
            {content.headline ?? (
              <>
                Wij monteren 'm <span style={{ fontStyle: "italic" }}>zonder een cent</span> extra.
              </>
            )}
          </h3>
          <p style={{ fontSize: 15, color: T.muted, lineHeight: 1.55, margin: "0 0 18px", maxWidth: 500 }}>
            {content.body ?? "Bij aanvraag deze maand: gratis montage door onze eigen vaste ploeg. U betaalt alleen de materialen."}
          </p>
          <Cta content={{ ...content, cta_text: content.cta_text ?? "Claim gratis montage" }} />
        </div>
      </div>
    </section>
  );
}

export default function KortingBarBlock({ content, variant = "editorial" }: Props) {
  switch (variant) {
    case "age-calculator":
      return <AgeCalculator content={content} />;
    case "voucher":
      return <Voucher content={content} />;
    case "dark-premium":
      return <DarkPremium content={content} />;
    case "oversized":
      return <Oversized content={content} />;
    case "sticker":
      return <Sticker content={content} />;
    case "editorial":
    default:
      return <Editorial content={content} />;
  }
}
